<template>
  <div
    class="top-comment"
    v-if="isTopComment && debate.votedSideId && finalArguments.length > 0"
  >
    <div class="top-comment-head">
      Top comment &bull;
      <template v-if="!globals.userInfo">
        <span class="text-button" @click="initLogin()">{{ $t("Login") }}</span>
        {{ $t("or") }}
        <span class="text-button" @click="initSignup()">{{
          $t("sign up")
        }}</span>
      </template>
      <span class="text-button" v-if="globals.userInfo" @click="onShareOpinion">
        {{ $t("Share your opinion") }}
      </span>
    </div>
  </div>

  <div
    class="create-argument"
    v-if="isSidePanel && !argumentAlreadyPosted()"
    :style="
      debate.votedSide()
        ? { borderLeftColor: debate.votedSide().sideColor }
        : {}
    "
  >
    <div class="create-argument-inner">
      <div v-if="!localForm.loading">
        <div class="create-argument-form">
          <address>
            <figure v-if="globals.userInfo && globals.userInfo.avatarObject">
              <img :src="globals.userInfo.avatarObject.thumb.location" />
            </figure>
            <figure v-else>
              <img
                src="https://cdn.sided.co/embed-assets/default-avatar-thumb.png"
              />
            </figure>
          </address>
          <address class="static" v-if="debate.votedSide()">
            <strong class="poll-author-name">{{ $t("you") }}</strong>
            {{ $t("voted") }}
            <SvgElement
              :pathFill="debate.votedSide().sideColor"
              name="vote-icon"
            />
            <strong>{{ debate.votedSide().text }}</strong>
          </address>
          <div class="create-argument-textarea">
            <textarea
              :placeholder="$t('Make your case!')"
              v-model="localForm.text"
              :class="{ 'mobile-field': isMobile() }"
            ></textarea>
          </div>
        </div>

        <div class="resource-label" v-if="false">
          <label>
            {{ localForm.links.length > 0 ? "Resource Link" : "" }}
          </label>
          <span @click="localForm.links.push({ text: '', link: '' })">
            + {{ $t("Add Link") }}
          </span>
        </div>
        <div
          v-for="link in localForm.links"
          class="link-field"
          v-bind:key="link.text"
        >
          <input type="text" placeholder="Add a quote..." v-model="link.text" />
          <input
            type="text"
            placeholder="Paste link here..."
            v-model="link.link"
          />
        </div>
        <div class="button-holder">
          <button
            class="blue-button font-size-s"
            :class="{
              bordered: !localForm.text.length,
              grayed: !localForm.text.length,
            }"
            @click="onPostArgument"
          >
            {{ $t("Comment") }}
          </button>
        </div>
      </div>
      <div v-if="localForm.loading">
        <em>{{ $t("Please wait") }}...</em>
      </div>
    </div>
  </div>

  <div class="arguments-list">
    <div
      class="arguments-list-item"
      v-for="(argument, index) in finalArguments"
      v-bind:key="argument.id"
    >
      <article :id="'argument-' + argument.id">
        <template v-if="argument.isUserDeleted">
          <div class="user-deleted-info">
            {{ $t("This argument has been removed") }}
          </div>
        </template>
        <template v-if="!argument.isUserDeleted">
          <div class="head-line">
            <address v-if="argument.user">
              <figure
                class="author-avatar"
                @click="openProfile(argument.user)"
                :style="{ borderColor: argument.user.statusColor }"
              >
                <img
                  v-if="argument.user && argument.user.avatarObject"
                  :src="argument.user.avatarObject.thumb.location"
                />
                <template
                  v-for="role in argument.user.roles"
                  v-bind:key="role.name"
                >
                  <span
                    v-if="role.name === 'VERIFIED'"
                    class="checkmark"
                    :style="{ backgroundColor: argument.user.checkmarkColor }"
                  >
                    <SvgElement name="check-icon" />
                  </span>
                </template>
              </figure>
              <strong
                class="poll-author-name"
                @click="openProfile(argument.user)"
              >
                {{ argument.user.username }}
              </strong>
              {{ $t("voted") }}
              <SvgElement
                :pathFill="argument.side.sideColor"
                name="vote-icon"
              />
              <strong>{{ argument.side.text }}</strong>
              &bull;
              {{ formatDate(argument.createdAt) }}
            </address>
          </div>
          <ul class="comments-list">
            <li>
              <div class="comment-side">
                <template
                  v-if="
                    argument.text.trim().length > globals.textMaxLength &&
                    !isSidePanel
                  "
                >
                  <div class="text-short">
                    {{
                      argument.text.substring(0, globals.textMaxLength).trim() +
                      "..."
                    }}
                  </div>
                  <span class="read-more" @click="readToggle($event, '+')">
                    {{ $t("Read more") }}
                  </span>
                  <div class="text-long">{{ argument.text }}</div>
                  <span class="read-less" @click="readToggle($event)">
                    {{ $t("Read less") }}
                  </span>
                </template>
                <template
                  v-if="
                    argument.text.trim().length <= globals.textMaxLength ||
                    isSidePanel
                  "
                >
                  {{ argument.text }}
                </template>
              </div>
              <div
                class="citation-link"
                v-for="citation in argument.citations"
                v-bind:key="citation.text"
              >
                <SvgElement name="icon-check" />
                <a
                  :href="
                    citation.link.indexOf('://') === -1
                      ? 'http://' + citation.link
                      : citation.link
                  "
                  target="_blank"
                  >{{ citation.text }}</a
                >
              </div>
            </li>
            <li>
              <div class="counters">
                <span
                  class="clickable"
                  @click="onLikeArgument('like', argument)"
                >
                  <SvgElement name="thumb-up-icon" v-if="argument.isLiked" />
                  <SvgElement
                    name="thumb-up-empty-icon"
                    v-if="!argument.isLiked"
                  />
                  <template v-if="argument.likesCount">
                    &nbsp;{{ argument.likesCount ? argument.likesCount : "" }}
                  </template>
                </span>
                <span
                  class="clickable"
                  @click="onLikeArgument('dislike', argument)"
                >
                  <SvgElement
                    name="thumb-down-icon"
                    v-if="argument.isDisliked"
                  />
                  <SvgElement
                    name="thumb-down-empty-icon"
                    v-if="!argument.isDisliked"
                  />
                  <template v-if="argument.dislikesCount">
                    &nbsp;{{
                      argument.dislikesCount ? argument.dislikesCount : ""
                    }}
                  </template>
                </span>
                <span
                  @click="onReply(argument)"
                  v-if="globals.userInfo"
                  class="clickable"
                >
                  <SvgElement name="reply-icon" />
                  {{ $t("Reply") }}
                </span>
                <span v-if="!globals.userInfo">
                  <span class="text-button" @click="onReply(argument)">{{
                    $t("Login")
                  }}</span>
                  to reply
                </span>
                <span
                  v-if="globals.userInfo"
                  class="clickable more-icon"
                  @click="showContentOptions('argument', argument)"
                >
                  <SvgElement name="circles-icon" />
                </span>
              </div>
            </li>
            <li
              class="view-all"
              v-if="commentsRemainedCount(argument) > 0"
              @click="onLoadComments(argument)"
            >
              <span v-if="argument.commentsExpanded.length === 0">
                {{ $t("View all") }} {{ commentsRemainedCount(argument) }}
                {{ $t("comments") }}
              </span>
              <span v-if="argument.commentsExpanded.length > 0">
                {{ $t("View") }} {{ commentsRemainedCount(argument) }}
                {{ $t("previous comments") }}
              </span>
            </li>
            <li
              class="padded"
              v-for="comment in argument.commentsExpanded"
              v-bind:key="comment.id"
            >
              <template v-if="comment.isUserDeleted">
                <div class="user-deleted-info">
                  {{ $t("This comment has been removed") }}
                </div>
              </template>
              <template v-if="!comment.isUserDeleted">
                <figure v-if="comment.user && comment.user.avatarObject">
                  <img :src="comment.user.avatarObject.thumb.location" />
                </figure>
                <div v-if="comment.user">
                  <address>
                    <strong>{{ comment.user.username }}</strong>
                    <template v-if="comment.votedSide">
                      voted
                      <SvgElement
                        :pathFill="comment.votedSide.sideColor"
                        name="vote-icon"
                      />
                      <strong>{{ comment.votedSide.text }}</strong>
                    </template>
                    &bull;
                    <span>{{ formatDate(comment.createdAt) }}</span>
                  </address>
                  <div
                    class="comment-body"
                    v-if="
                      comment.text.trim().length > globals.textMaxLength &&
                      !isSidePanel
                    "
                  >
                    <div class="text-short">
                      {{
                        comment.text
                          .substring(0, globals.textMaxLength)
                          .trim() + "..."
                      }}
                    </div>
                    <span class="read-more" @click="readToggle($event, '+')">
                      {{ $t("Read more") }}
                    </span>
                    <div class="text-long">
                      {{ comment.text }}
                    </div>
                    <span class="read-less" @click="readToggle($event)">
                      {{ $t("Read less") }}
                    </span>
                  </div>
                  <div
                    class="comment-body"
                    v-if="
                      comment.text.trim().length <= globals.textMaxLength ||
                      isSidePanel
                    "
                  >
                    {{ comment.text }}
                  </div>
                </div>
                <div class="comment-bar">
                  <span
                    class="clickable"
                    @click="onLikeComment('like', comment)"
                  >
                    <SvgElement name="thumb-up-icon" v-if="comment.isLiked" />
                    <SvgElement
                      name="thumb-up-empty-icon"
                      v-if="!comment.isLiked"
                    />
                    <template v-if="comment.likesCount">
                      &nbsp;{{ comment.likesCount ? comment.likesCount : "" }}
                    </template>
                  </span>
                  <span
                    class="clickable"
                    @click="onLikeComment('dislike', comment)"
                  >
                    <SvgElement
                      name="thumb-down-icon"
                      v-if="comment.isDisliked"
                    />
                    <SvgElement
                      name="thumb-down-empty-icon"
                      v-if="!comment.isDisliked"
                    />
                    <template v-if="comment.dislikesCount">
                      &nbsp;{{
                        comment.dislikesCount ? comment.dislikesCount : ""
                      }}
                    </template>
                  </span>
                  <span class="clickable" @click="onReply(argument, comment)">
                    <SvgElement name="reply-icon" />
                    {{ $t("Reply") }}
                  </span>
                  <span
                    v-if="globals.userInfo"
                    class="clickable more-icon"
                    @click="showContentOptions('comment', comment)"
                  >
                    <SvgElement name="circles-icon" />
                  </span>
                </div>
                <CreateCommentForm
                  :debate="debate"
                  v-if="replyCommentId === comment.id && globals.userInfo"
                />
              </template>
            </li>
            <li
              class="view-all"
              v-if="argument.commentsExpanded.length > 0"
              @click="argument.commentsExpanded = []"
            >
              <span>
                {{ $t("Hide all comments") }}
              </span>
            </li>
          </ul>

          <CreateCommentForm
            :debate="debate"
            v-if="
              replyArgumentId === argument.id &&
              replyCommentId === 0 &&
              globals.userInfo
            "
          />
        </template>
      </article>

      <div class="ad-2" v-if="!adsHidden && isSidePanel">
        <div v-if="index % 1 === 0 && (index > 0 || isTopComment)">
          <div
            v-if="adsAdSense"
            class="in-between-ad"
            v-bind:id="'div-gpt-ad-1619577005746-' + index"
          ></div>
          <div v-if="!adsAdSense">
            <div
              class="in-between-ad"
              v-if="!isMobile()"
              v-bind:id="'dsk-sided-ad-' + getLetter(index)"
            ></div>
            <div
              class="in-between-ad mask"
              v-if="isMobile()"
              v-bind:id="'mob-sided-ad-' + getLetter(index)"
            ></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import SvgElement from "./SvgElement";
import CreateCommentForm from "./CreateCommentForm";

export default {
  name: "ArgumentsFeed",
  data() {
    return {
      localForm: {
        text: "",
        links: [],
        loading: false,
      },
      argumentJustPosted: false,
      alphabet: [
        "a",
        "b",
        "c",
        "d",
        "e",
        "f",
        "g",
        "h",
        "i",
        "j",
        "k",
        "l",
        "m",
        "n",
        "o",
        "p",
        "q",
        "r",
        "s",
        "t",
        "u",
        "v",
        "w",
        "x",
        "y",
        "z",
      ],
    };
  },
  props: {
    debate: {
      type: Object,
      default: null,
    },
    isSidePanel: {
      type: Boolean,
      default: false,
    },
    isTopComment: {
      type: Boolean,
      default: false,
    },
    justFirstComment: {
      type: Boolean,
      default: false,
    },
    isCustomArgumentsFeed: {
      type: Boolean,
      default: false,
    },
    feedDebateArguments: {
      type: Array,
      default: () => [],
    },
  },
  inject: [
    "globals",
    "isMobile",
    "initLogin",
    "fireEvent",
    "arguments",
    "adsHidden",
    "initSignup",
    "adsAdSense",
    "setGlobals",
    "loadDebate",
    "openProfile",
    "fireEventGA",
    "onLikeComment",
    "onLikeArgument",
    "onShowSidePanel",
    "refreshAuthToken",
    "showContentOptions",
    "replyArgumentId",
    "replyCommentId",
    "replyToArgument",
    "replyToComment",
  ],
  components: {
    SvgElement,
    CreateCommentForm,
  },
  computed: {
    finalArguments() {
      const items = this.isCustomArgumentsFeed
        ? this.feedDebateArguments
        : this.arguments;

      if (this.justFirstComment && items.length > 0) {
        return items.slice(0, 1);
      }

      return items;
    },
  },
  methods: {
    argumentAlreadyPosted() {
      if (this.argumentJustPosted) {
        return true;
      }

      return (
        this.globals.userKey &&
        this.finalArguments.find(
          ({ userId }) => userId === parseInt(this.globals.userKey),
        )
      );
    },
    getLetter(index) {
      return this.alphabet[Math.floor(index / 4) + 1];
    },
    hexToRgb(hex) {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

      return result
        ? parseInt(result[1], 16) +
            "," +
            parseInt(result[2], 16) +
            "," +
            parseInt(result[3], 16)
        : null;
    },
    formatDate(date) {
      return new Date(date).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
      });
    },
    onReply(argument, comment = null) {
      this.replyToArgument(argument.id);
      this.replyToComment(comment ? comment.id : 0);

      if (!this.globals.userInfo) {
        this.initLogin();
      }
    },
    onPostArgument() {
      if (!this.globals.userInfo) {
        this.initLogin();
      } else if (!this.debate.votedSide()) {
        alert(this.$t("Please vote first"));
      } else if (this.localForm.text.trim().length === 0) {
        alert(this.$t("Please enter the argument text"));
      } else {
        this.localForm.loading = true;

        axios
          .post("/argument/create", {
            argument: this.localForm.text,
            debateId: this.debate.id,
            side: this.debate.votedSideId ? this.debate.votedSideId : 0,
            citations: this.localForm.links
              .filter(
                (link) => link.text.trim() !== "" || link.link.trim() !== "",
              )
              .map((link) => {
                return { text: link.text, link: link.link };
              }),
            domain: this.debate.siteDomain,
            pageUrl: window.location.href,
          })
          .then(
            () => {
              this.fireEvent({
                debateId: this.debate.id,
                sideId: this.debate.votedSideId ? this.debate.votedSideId : 0,
                userId: this.globals.userKey,
                fingerprintId: this.globals.deviceId,
                eventType: "argument",
              });

              this.argumentJustPosted = true;
              this.localForm = {
                loading: false,
                text: "",
                links: [{ text: "", link: "" }],
              };
              this.loadDebate(null, true, this.debate.id);

              window.sidedAnalytics.logEvent("create_argument", {
                pagename: this.debate.thesis,
              }); // eslint-disable-line
              this.fireEventGA("create_argument", {
                pagename: this.debate.thesis,
              });
            },
            ({ response }) => {
              if (response.status === 401) {
                this.refreshAuthToken(() => this.onPostArgument(), true);
                this.localForm.loading = false;
              } else {
                this.localForm.loading = false;

                setTimeout(function () {
                  alert(Object.values(response.data.message[0])[0]);
                });
              }
            },
          );
      }
    },
    onSetTextareaHeight(event) {
      const field = event.target;
      field.style.height = "inherit";
      field.style.height = field.scrollHeight - 14 + "px";
    },
    commentsRemainedCount(argument) {
      return argument.comments.length - argument.commentsExpanded.length;
    },
    onLoadComments(argument) {
      axios
        .get(
          `/comment?argumentId=${
            argument.id
          }&perPage=5&pageNumber=${argument.commentsPageToLoadNext()}`,
        )
        .then((response) => {
          const { data, authorVotes } = response.data;

          data.map((comment) => {
            const authorVote = authorVotes.find(
              ({ userId }) => userId === comment.userId,
            );

            if (authorVote) {
              comment.votedSide = this.debate.sides.find(
                ({ id }) => authorVote.sideId === id,
              );
            }

            argument.commentsExpanded.unshift(comment);
          });
        });
    },
    onShareOpinion() {
      if (!this.isSidePanel) {
        this.onShowSidePanel(this.debate);
      }
    },
  },
};
</script>
